import AddCompanyModal from '@features/enterprise/components/AddCompanyModal';
import AssignCompanyModal from '@features/enterprise/components/AssignCompanyModal';
import CompaniesList from '@features/enterprise/components/CompaniesList';
import TemplateApplicationResponsesTab from '@features/enterprise/components/TemplateApplicationResponsesTab';
import TemplateApplicationSettingsTab from '@features/enterprise/components/TemplateApplicationSettingsTab';
import TemplateGenralSettings from '@features/enterprise/components/TemplateGenralSettings';
import TemplateHireflixInterviewSettingsTab from '@features/enterprise/components/TemplateHireflixInterviewSettingsTab';
import TemplateInterviewSettings from '@features/enterprise/components/TemplateInterviewSettings';
import TemplateJobPageCustomizationTab from '@features/enterprise/components/TemplateJobPageCustomizationTab';
import TemplatePaymentMethod from '@features/enterprise/components/TemplatePaymentMethod';
import TemplatePositionList from '@features/enterprise/components/TemplatePositionList';
import TemplateQulificationQuestions from '@features/enterprise/components/TemplateQulificationQuestions';
import TemplateSavedMessagesTab from '@features/enterprise/components/TemplateSavedMessagesTab';
import TemplateTagsManagement from '@features/enterprise/components/TemplateTagsManagement';
import UploadCsvModal from '@features/enterprise/components/UploadCsvModal';
import { useGetTemplates } from '@features/enterprise/hooks/useGetTemplates';
import { EnterpriseTemplate } from '@features/enterprise/types';
import { Upload } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { darkBlue, white } from '@styles/colors';
import { bold } from '@styles/fontWeights';
import React, { Fragment, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type SettingTab = {
  label: string;
  isOpen: boolean;
  tabs: string[];
};

const initialOpenTabs = {
  'General Settings': true,
  'Company Settings': false,
  'Attached Companies': false,
  Billing: false,
};

const TampleteDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const hash = location.hash.replace('#', '').replace(/-/g, ' ');
  const [openTabs, setOpenTabs] = useState(initialOpenTabs);
  const [errorText, setErrorText] = useState('');
  const [addCompanyModal, setAddCompanyModal] = useState(false);
  const [assignCompanyModal, setAssignCompanyModal] = useState(false);
  const [uploadCsvModal, setUploadCsvModal] = useState(false);
  const { templatesList, isLoadingTemplatesList } = useGetTemplates({
    setErrorText,
    templateId: id,
  });

  const templateData = templatesList?.object as
    | EnterpriseTemplate
    | null
    | undefined;

  const settingTabs: SettingTab[] = [
    {
      label: 'General Settings',
      isOpen: openTabs['General Settings'],
      tabs: ['Template'],
    },
    {
      label: 'Company Settings',
      isOpen: openTabs['Company Settings'],
      tabs: [
        'Jobs Page Customization',
        'Interview Settings',
        'Hireflix Interview Settings',
        'Application Responses',
        'Hiring Automation',
        'Saved Messages',
        'Manage Tags',
        'Job Management',
        'Application Questions',
      ],
    },
    {
      label: 'Billing',
      isOpen: openTabs.Billing,
      tabs: ['Billing Details'],
    },
    {
      label: 'Attached Companies',
      isOpen: openTabs['Attached Companies'],
      tabs: ['Companies List'],
    },
  ];

  const allTabs = settingTabs.flatMap((tab) => tab.tabs);
  const [selectedTab, setSelectedTab] = useState<
    (typeof allTabs | 'WOTC' | 'Onboarding')[number]
  >(hash || allTabs[0]);

  const getTab = () => {
    switch (selectedTab) {
      case 'Template':
        return templateData ? (
          <TemplateGenralSettings templateData={templateData} />
        ) : null;
      case 'Jobs Page Customization':
        return templateData ? (
          <TemplateJobPageCustomizationTab templateData={templateData} />
        ) : null;
      case 'Interview Settings':
        return templateData ? (
          <TemplateInterviewSettings templateData={templateData} />
        ) : null;
      case 'Hireflix Interview Settings':
        return templateData ? (
          <TemplateHireflixInterviewSettingsTab templateData={templateData} />
        ) : null;
      case 'Application Responses':
        return templateData ? (
          <TemplateApplicationResponsesTab templateData={templateData} />
        ) : null;
      case 'Job Management':
        return templateData ? (
          <TemplatePositionList templateData={templateData} />
        ) : null;
      case 'Application Questions':
        return templateData ? (
          <TemplateQulificationQuestions templateData={templateData} />
        ) : null;
      case 'Saved Messages':
        return templateData ? (
          <TemplateSavedMessagesTab templateData={templateData} />
        ) : null;
      case 'Hiring Automation':
        return templateData ? (
          <TemplateApplicationSettingsTab templateData={templateData} />
        ) : null;
      case 'Companies List':
        return templateData ? (
          <CompaniesList templateData={templateData} />
        ) : null;
      case 'Billing Details':
        return templateData ? (
          <TemplatePaymentMethod templateData={templateData} />
        ) : null;
      case 'Manage Tags':
        return templateData ? (
          <TemplateTagsManagement templateData={templateData} />
        ) : null;

      default:
        return null;
    }
  };
  useEffect(() => {
    if (hash && allTabs.includes(hash)) {
      setSelectedTab(hash);
      const currentSettingTab = settingTabs.find((settingTab) =>
        settingTab.tabs.some((subTab) => subTab === hash),
      );

      if (currentSettingTab) {
        setOpenTabs((prev) => ({ ...prev, [currentSettingTab.label]: true }));
      }
    }
  }, [hash]);

  return (
    <div>
      <Helmet>
        <title>Template | StaffedUp</title>
      </Helmet>
      <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>
        {isLoadingTemplatesList && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              bgcolor: 'white',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!isLoadingTemplatesList && errorText && (
          <Alert severity="error">{errorText}</Alert>
        )}

        {!isLoadingTemplatesList && !errorText && templatesList && (
          <>
            <Stack
              p={3}
              borderRadius={8}
              bgcolor={white}
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
            >
              <Stack
                spacing={2}
                maxWidth={{ sm: '100%', md: '50%' }}
                sx={{ wordBreak: 'break-all' }}
                mb={{ sm: 3, md: 0 }}
              >
                <Button
                  variant="text"
                  onClick={() => navigate('../enterprise/templates')}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    textDecoration: 'underline',
                    width: 'fit-content',
                  }}
                >
                  Back
                </Button>
                <Typography variant="h3" fontSize="28px" fontWeight="bold">
                  {templatesList?.object?.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: '14px',
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: templatesList?.object?.description,
                    }}
                  />
                </Typography>
              </Stack>
              <Stack
                direction="row"
                maxWidth={{ sm: '100%', md: '50%' }}
                spacing={5}
                alignItems="center"
              >
                {templatesList?.object?.status === 0 && (
                  <Stack spacing={5} direction={{ xs: 'column', md: 'row' }}>
                    <Button
                      size="small"
                      onClick={() => setAddCompanyModal(true)}
                      startIcon={<AddIcon />}
                      variant="contained"
                      sx={{
                        height: 'fit-content',
                        mt: 2,
                        width: { xs: '100%', md: 'fit-content' },
                      }}
                    >
                      Add Company Using Template
                    </Button>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        size="small"
                        onClick={() => setUploadCsvModal(true)}
                        startIcon={<Upload />}
                        variant="contained"
                        color="secondary"
                        sx={{
                          height: 'fit-content',
                          mt: 2,
                          width: {
                            xs: '100%',
                            md: 'fit-content',
                            textTransform: 'none',
                          },
                        }}
                      >
                        Import Companies via CSV
                      </Button>
                      <Tooltip
                        title={
                          <span>
                            Upload a CSV file to bulk add companies using a
                            template. Ensure the format matches the sample file.
                            -{' '}
                            <a
                              href={`${import.meta.env.VITE_S3_PATH}assets/csv/Enterprise-CSV-Template.csv`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                cursor: 'pointer',
                                fontWeight: bold,
                                color: darkBlue,
                                textDecoration: 'underline',
                              }}
                            >
                              Download Sample
                            </a>
                          </span>
                        }
                        placement="top"
                        arrow
                      >
                        <HelpOutlineIcon color="secondary" fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
            <Alert severity="info">
              Future edits to this template will{' '}
              {!templateData?.apply_changes && 'not'} affect{' '}
              {templateData?.apply_changes && 'all'} companies attached.
            </Alert>
            <Grid container>
              <Grid
                item
                xs={12}
                sm={4}
                md={3}
                pr={{ xs: 0, sm: 3 }}
                pb={{ xs: 3, sm: 0 }}
                sx={{
                  borderRadius: 8,
                }}
              >
                <List
                  sx={{
                    bgcolor: 'secondary.main',
                    color: white,
                    borderRadius: 8,
                  }}
                  component="nav"
                >
                  {settingTabs.map((settingTab) => (
                    <Fragment key={settingTab.label}>
                      <ListItem
                        id={`parent-button-${settingTab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        button
                        onClick={() =>
                          setOpenTabs((prev) => ({
                            ...prev,
                            [settingTab.label]: !settingTab.isOpen,
                          }))
                        }
                      >
                        <ListItemText
                          primary={settingTab.label}
                          primaryTypographyProps={{
                            variant: 'h4',
                            fontSize: '16px',
                          }}
                        />
                        {settingTab.isOpen ? (
                          <ExpandLessIcon style={{ fontSize: '20px' }} />
                        ) : (
                          <ExpandMoreIcon style={{ fontSize: '20px' }} />
                        )}
                      </ListItem>
                      <Collapse
                        in={settingTab.isOpen}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List
                          disablePadding
                          sx={{
                            bgcolor: white,
                            color: 'text.primary',
                          }}
                        >
                          {settingTab.tabs.map((tab) => (
                            <ListItem
                              key={tab}
                              id={`child-button-${tab.toLowerCase().replace(/\s+/g, '-')}`}
                              button
                              sx={{ pl: 4, py: '5px' }}
                              onClick={() => {
                                setSelectedTab(tab);
                                navigate(`#${tab.replace(/\s+/g, '-')}`);
                              }}
                              selected={selectedTab === tab}
                            >
                              <ListItemText
                                primary={tab}
                                primaryTypographyProps={{
                                  variant: 'subtitle1',
                                  fontSize: '15px',
                                  fontWeight: '500',
                                }}
                              />
                              <KeyboardArrowRightIcon
                                style={{ fontSize: '20px' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Fragment>
                  ))}
                </List>
              </Grid>
              <Grid
                sx={{
                  borderRadius: 8,
                }}
                item
                xs={12}
                sm={8}
                md={9}
              >
                {getTab()}
              </Grid>
            </Grid>
          </>
        )}
      </Stack>

      {addCompanyModal && (
        <AddCompanyModal
          templateId={templatesList?.object.id}
          handleClose={() => {
            setAddCompanyModal(false);
          }}
          billingType={templatesList?.object?.payment_method}
        />
      )}

      {assignCompanyModal && (
        <AssignCompanyModal
          templateId={templatesList?.object.id}
          handleClose={() => {
            setAssignCompanyModal(false);
          }}
        />
      )}

      {uploadCsvModal && (
        <UploadCsvModal
          templateId={templatesList?.object.id}
          handleClose={() => {
            setUploadCsvModal(false);
          }}
          open={uploadCsvModal}
          billingType={templatesList?.object?.payment_method}
        />
      )}
    </div>
  );
};

export default TampleteDetail;
